import React, { useState, useEffect, useRef } from "react";

export type LoadMoreProps = {
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  loadedCount: number;
  totalCount: number;
  pageSize?: number;
  className?: string;
  contentRef?: React.RefObject<HTMLElement | null>;
};

const LoadMore = ({
  hasMore,
  isLoading,
  onLoadMore,
  loadedCount,
  totalCount,
  pageSize = 12,
  className = "",
  contentRef,
}: LoadMoreProps) => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const prevLoadedCountRef = useRef(loadedCount);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleClick = () => {
    if (isLoading || !hasMore) return;

    // Smoothly load more without scrolling
    onLoadMore();
  };

  // Removed auto-scroll effect to prevent jarring page jumps

  const remaining = Math.max(0, totalCount - loadedCount);
  const loadAmount = Math.min(pageSize, remaining);

  return (
    <>
      <div
        ref={loadMoreRef}
        className={`flex flex-col items-center gap-3 py-6 ${className}`}
      >
        {hasMore && !isLoading && (
          <button
            onClick={handleClick}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#2D96A7] hover:bg-[#2D96A7]/90 text-white text-sm font-semibold rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
          >
            Load More
          </button>
        )}

        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <svg
              className="animate-spin h-5 w-5 text-[#2D96A7]"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Loading...
          </div>
        )}
{
  /*
  
        {!hasMore && loadedCount > 0 && (
          <p className="text-sm text-gray-400">
            You&apos;ve reached the end ({loadedCount} items)
          </p>
        )}
  
  */
}
      </div>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-20 right-4 z-40 w-12 h-12 bg-white border border-gray-200 rounded-full shadow-lg flex items-center justify-center text-[#2D96A7] hover:bg-[#2D96A7] hover:text-white transition-all duration-200"
          aria-label="Scroll to top"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 15l7-7 7 7"
            />
          </svg>
        </button>
      )}
    </>
  );
};

export default LoadMore;
